import React from 'react';
import I18n from 'i18n-js';
import { Location } from 'history';
import { camelCase, omit, sortBy } from 'lodash';

import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { RCButton, RCSelect } from '@reverbdotcom/cadence/components';

import {
  PRICE_GUIDE_TOOL_CONDITIONS,
  conditionUuidForSlug,
  conditionSlugForUuid,
} from '@reverbdotcom/commons/src/condition_helpers';
import SiteModule from '@reverbdotcom/commons/src/components/site_module';
import { useHelpCenterArticleUrl } from '@reverbdotcom/commons/src/url_helpers';
import {
  IFormFieldsState,
  IFormFieldsAction,
  IFormFieldsActionType,
  CONDITION_UUID_FORM_FIELDS_STATE_KEY,
  CANONICAL_PRODUCT_FORM_FIELDS_STATE_KEY,
} from './price_guide_tool_form';
import { CSP } from '@reverbdotcom/commons/src/gql/graphql';
import URLHelpers from '../../shared/url_helpers';
import { SanitizedRender } from '@reverbdotcom/commons';
import { GEAR_COLLECTION_QUERY_PARAM_KEYS_FOR_REDIRECTS } from './price_guide_tool_gear_collection_button';

const HELP_CENTER_CONDITION_ARTICLE_ID = '360013525833';

interface IExternalProps {
  csp: CSP;
  location?: Location;
  formFieldsState: IFormFieldsState;
  dispatch: (action: IFormFieldsAction) => void;
  updateShowEstimate: (value: boolean) => void;
}

export function ConditionHelpText({ conditionUuid }) {
  const hasSelectedCondition = !!conditionUuid;
  const hyperLinkText = hasSelectedCondition ? 'discovery.priceGuideTool.show.form.conditionHelpTextSelected' : 'discovery.priceGuideTool.show.form.conditionHelpText';

  return (
    <div className="mt-2">
      <p>
        {hasSelectedCondition && (
          <>
            <SanitizedRender
              html={I18n.t(`discovery.priceGuideTool.show.form.conditionExplanation.${camelCase(conditionSlugForUuid(conditionUuid))}`, {
                span_open: '<b>',
                span_close: '</b>',
                no_break: '&nbsp;',
              })}
            />
            &nbsp;
          </>
        )}

        <CoreLink
          to={useHelpCenterArticleUrl(HELP_CENTER_CONDITION_ARTICLE_ID)}
          className="ph ph-r ph-arrow-up-right weight-bold"
          target="_blank"
        >
          {I18n.t(hyperLinkText)}
        </CoreLink>
      </p>
    </div>
  );
}

export const COMPONENT_NAME = 'PriceGuideToolFormFields';

export function FormFieldsFooter({
  location,
  updateShowEstimate,
  eligibleToPriceGear,
  csp,
  canonicalProductId,
  conditionUuid,
}) {
  const query = location?.query || {};
  const sanitizeQuery = omit(query, GEAR_COLLECTION_QUERY_PARAM_KEYS_FOR_REDIRECTS);

  const priceGear = () => {
    trackEvent({
      eventName: MParticleEventName.ClickedPriceGearButtonInPriceGuideTool,
      componentName: COMPONENT_NAME,
      cspId: csp.id,
      cspSlug: csp.slug,
      cpId: canonicalProductId,
      conditionSlug: conditionSlugForUuid(conditionUuid),
    });
    updateShowEstimate(true);
  };

  return (
    <div className="d-flex fx-justify-between fx-align-center mobile-fx-dir-col mobile-width-100">
      <CoreLink
        className="ph ph-l ph-arrow-left weight-bold mobile-mt-2 mobile-mb-4"
        to={URLHelpers.priceGuideSearchPath(sanitizeQuery)}
        clickEvent={{
          eventName: MParticleEventName.ClickedBackButtonToPriceGuideToolIndex,
          componentName: COMPONENT_NAME,
          cspId: csp.id,
          cspSlug: csp.slug,
          cpId: canonicalProductId,
          conditionSlug: conditionSlugForUuid(conditionUuid) || '',
        }}
      >
        {I18n.t('discovery.priceGuideTool.show.form.backButtonText')}
      </CoreLink>

      <RCButton
        variant="filled"
        onClick={priceGear}
        disabled={!eligibleToPriceGear}
      >
        {I18n.t('discovery.priceGuideTool.show.form.priceGearButtonText')}
      </RCButton>
    </div>
  );
}

export function ModelHelpText({ hasMultipleCanonicalProducts }) {
  if (hasMultipleCanonicalProducts) return null;

  return (
    <div className="mt-2">
      <p>
        {I18n.t('discovery.priceGuideTool.show.form.modelHelpText')}
      </p>
    </div>
  );
}

export default function PriceGuideToolFormFields({
  csp,
  location,
  formFieldsState,
  dispatch,
  updateShowEstimate,
}: IExternalProps) {
  const canonicalProductId = formFieldsState[CANONICAL_PRODUCT_FORM_FIELDS_STATE_KEY].id || '';
  const conditionUuid = formFieldsState[CONDITION_UUID_FORM_FIELDS_STATE_KEY];

  const { canonicalProducts } = csp;
  const hasMultipleCanonicalProducts = canonicalProducts.length > 1;
  const sortedCanonicalProducts = sortBy(canonicalProducts, ({ name }) => name);
  const eligibleToPriceGear = canonicalProductId.length && conditionUuid.length;

  return (
    <SiteModule
      title={I18n.t('discovery.priceGuideTool.show.form.gearDetails')}
      footerContent={<FormFieldsFooter
        location={location}
        updateShowEstimate={updateShowEstimate}
        eligibleToPriceGear={eligibleToPriceGear}
        csp={csp}
        canonicalProductId={canonicalProductId}
        conditionUuid={conditionUuid}
      />}
    >
      <RCSelect
        id="modelVersion"
        name="modelVersion"
        label={I18n.t('discovery.priceGuideTool.show.form.modelVersion')}
        onChange={event => dispatch({
          type: IFormFieldsActionType.UPDATE,
          payload: {
            [CANONICAL_PRODUCT_FORM_FIELDS_STATE_KEY]: {
              ...canonicalProducts.find(({ id }) => id == event.target.value),
            },
          },
        })}
        addEmptyOption
        emptyOptionLabel={I18n.t('discovery.priceGuideTool.show.form.selectModel')}
        value={canonicalProductId}
        disabled={!hasMultipleCanonicalProducts}
        helpText={<ModelHelpText hasMultipleCanonicalProducts={hasMultipleCanonicalProducts}/>}
      >
        {sortedCanonicalProducts.map(({ id, name }) => (
          <RCSelect.Option
            key={`variant-${id}`}
            value={id}
            label={name}
          />
        ))}
      </RCSelect>

      <div className="g-container">
        <div className="g-col-12 g-col-tablet-12">
          <RCSelect
            id="condition"
            name="condition"
            label={I18n.t('discovery.priceGuideTool.show.form.condition')}
            onChange={event => dispatch({
              type: IFormFieldsActionType.UPDATE,
              payload: {
                [CONDITION_UUID_FORM_FIELDS_STATE_KEY]: event.target.value,
              },
            })}
            addEmptyOption
            emptyOptionLabel={I18n.t('discovery.priceGuideTool.show.form.selectCondition')}
            value={conditionUuid}
            helpText={<ConditionHelpText conditionUuid={conditionUuid} />}
          >
            {PRICE_GUIDE_TOOL_CONDITIONS.map(conditionSlug => (
              <RCSelect.Option
                key={`condition-${conditionSlug}`}
                value={conditionUuidForSlug(conditionSlug)}
                label={I18n.t(`discovery.conditions.${conditionSlug}`)}
              />
            ))}
          </RCSelect>
        </div>
      </div>

      <p className="color-light-gray ph ph-l ph-lock size-90 mt-2 mb-0">
        {I18n.t('discovery.priceGuideTool.show.form.disclaimer')}
      </p>
    </SiteModule>
  );
}
